import { useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Server, Users, ScrollText, AlertTriangle, LogOut, ChevronLeft, Bell, Search, Shield, LayoutGrid, UserCog, Languages, FileBarChart, Archive, CreditCard, Mail, Activity, HelpCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { LanguageSelector } from "@/components/LanguageSelector";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";

const LOGO_URL = "https://assets-h2.izmocars.com/dealerlogos/qcoresecure.com.png";

const NAV_ITEMS = [
  { path: "/admin", label: "nav.system_overview", icon: LayoutDashboard },
  { path: "/admin/dashboard", label: "nav.live_dashboard", icon: Activity },
  { path: "/admin/devices", label: "nav.device_management", icon: Server },
  { path: "/admin/tenants", label: "nav.tenant_management", icon: Users },
  { path: "/admin/audit-logs", label: "nav.audit_logs", icon: ScrollText },
  { path: "/admin/alerts", label: "nav.alerts", icon: AlertTriangle },
];

const CONFIG_ITEMS = [
  { path: "/admin/widgets", label: "nav.widget_builder", icon: LayoutGrid },
  { path: "/admin/reports", label: "nav.report_builder", icon: FileBarChart },
  { path: "/admin/roles", label: "nav.role_management", icon: UserCog },
  { path: "/admin/plans", label: "nav.plan_management", icon: CreditCard },
  { path: "/admin/translations", label: "nav.translations", icon: Languages },
  { path: "/admin/email-config", label: "nav.email_config", icon: Mail },
  { path: "/admin/data-archival", label: "nav.data_archival", icon: Archive },
  { path: "/admin/help", label: "nav.help", icon: HelpCircle },
];

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { t } = useLanguage();
  const { user, logout } = useAuth();

  const handleLogout = async () => { await logout(); navigate("/login"); };
  const initials = user ? `${user.first_name?.[0] || ""}${user.last_name?.[0] || ""}`.toUpperCase() : "AD";

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className={`${sidebarCollapsed ? 'w-16' : 'w-64'} bg-slate-900 flex flex-col transition-all duration-200 fixed top-0 left-0 bottom-0 z-40`} data-testid="admin-sidebar">
        <div className={`h-16 border-b border-slate-800 flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'px-5'}`}>
          <Link to="/" className="flex items-center gap-2.5" data-testid="admin-sidebar-logo">
            {sidebarCollapsed ? (
              <div className="w-7 h-7 bg-cyan-500 flex items-center justify-center flex-shrink-0">
                <span className="text-black font-bold text-[10px]">QC</span>
              </div>
            ) : (
              <div>
                <img src={LOGO_URL} alt="QCoreSecure" className="h-5 object-contain" />
                <span className="text-[9px] uppercase tracking-[0.15em] text-cyan-400 block mt-0.5">Admin Console</span>
              </div>
            )}
          </Link>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto">
          <div className={`${sidebarCollapsed ? 'px-2' : 'px-3'} mb-6`}>
            {!sidebarCollapsed && <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-medium px-3 mb-2">Operations</div>}
            {NAV_ITEMS.map(item => {
              const isActive = location.pathname === item.path;
              const label = t(item.label);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2 mb-0.5 text-sm transition-colors ${
                    isActive ? 'bg-cyan-500/10 text-cyan-400 border-l-2 border-cyan-400 -ml-px' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                  } ${sidebarCollapsed ? 'justify-center' : ''}`}
                  data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  {!sidebarCollapsed && <span>{label}</span>}
                </Link>
              );
            })}
          </div>
          <div className={`${sidebarCollapsed ? 'px-2' : 'px-3'} mb-6`}>
            {!sidebarCollapsed && <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-medium px-3 mb-2">Configuration</div>}
            {CONFIG_ITEMS.map(item => {
              const isActive = location.pathname === item.path;
              const label = t(item.label);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2 mb-0.5 text-sm transition-colors ${
                    isActive ? 'bg-cyan-500/10 text-cyan-400 border-l-2 border-cyan-400 -ml-px' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                  } ${sidebarCollapsed ? 'justify-center' : ''}`}
                  data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  {!sidebarCollapsed && <span>{label}</span>}
                </Link>
              );
            })}
          </div>
          {!sidebarCollapsed && (
            <div className="px-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-medium px-3 mb-2">Switch Portal</div>
              <Link to="/dashboard" className="flex items-center gap-3 px-3 py-2 text-sm text-slate-400 hover:bg-slate-800 hover:text-slate-200 transition-colors" data-testid="admin-nav-customer-portal">
                <ChevronLeft className="w-4 h-4" />
                <span>{t("nav.customer_portal")}</span>
              </Link>
            </div>
          )}
        </nav>
        <div className={`border-t border-slate-800 p-3 ${sidebarCollapsed ? 'flex justify-center' : ''}`}>
          {!sidebarCollapsed && (
            <div className="flex items-center gap-3 px-2">
              <div className="w-8 h-8 bg-slate-800 border border-slate-700 flex items-center justify-center text-xs font-bold text-cyan-400">{initials}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white truncate">{user ? `${user.first_name} ${user.last_name}` : "Admin User"}</div>
                <div className="text-xs text-slate-500 truncate capitalize">{user?.role?.replace("_", " ") || "Super Administrator"}</div>
              </div>
              <button onClick={handleLogout} className="p-1 text-slate-500 hover:text-slate-300" data-testid="admin-logout-btn">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <div className={`flex-1 ${sidebarCollapsed ? 'ml-16' : 'ml-64'} transition-all duration-200`}>
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 sticky top-0 z-30" data-testid="admin-topbar">
          <div className="flex items-center gap-4">
            <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors" data-testid="admin-sidebar-toggle">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" /></svg>
            </button>
            <div className="relative hidden sm:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search devices, tenants..." className="w-72 h-9 pl-9 bg-slate-50 border-slate-200 text-sm" data-testid="admin-topbar-search" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSelector variant="compact" />
            <Badge className="bg-slate-900 text-white text-xs font-mono uppercase tracking-wider" data-testid="admin-role-badge">
              <Shield className="w-3 h-3 mr-1.5" /> Admin
            </Badge>
            <button className="relative p-2 text-slate-400 hover:text-slate-600 transition-colors" data-testid="admin-notifications-btn">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-amber-500 rounded-full" />
            </button>
          </div>
        </header>
        <main className="p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
